import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useUserState } from '../query/UserProvider';
import { extractJobsFromUserState } from '../utils/user-state/extract-jobs-from-user-state';
import { buildJobFlowSankey } from '../utils/data/build-job-flow-sankey';
import { hasAnyPositiveLink, scaleSankeyData } from '../utils/data/sankey';
import { isBrowsedJobKey } from '../types/user-state';
import type { UserStateMap } from '../types/user-state';
import type { SankeyData } from '../types/visualization';
import { useFormatMessage } from '../i18n-review/client/Msg';

function extractBrowsedJobsFromUserState(userState: UserStateMap | undefined): Array<{ id: string }> {
  if (!userState) return [];
  const browsed: Array<{ id: string }> = [];
  Object.entries(userState).forEach(([itemKey, entry]) => {
    if (!isBrowsedJobKey(itemKey) || !entry?.data) return;
    const entryData = entry.data as Record<string, unknown>;
    const jobId = entryData.id ?? entryData.objectID;
    if (jobId) {
      browsed.push({ id: String(jobId) });
    }
  });
  return browsed;
}

/**
 * TEC-404: migrated off Redux onto the shared TanStack userState cache (`useUserState()`) — name
 * kept as-is (still `...FromRedux`) to avoid touching its two call sites for a rename alone,
 * matching how `useApplicationKitActions.ts`/`ApplicationStateSelector.tsx` kept their names
 * through the same migration.
 */
export function useJobFlowSankeyFromRedux(): { scaledData: SankeyData | null; hasData: boolean } {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const { data: userState } = useUserState();
  const { viewedJobs, savedJobs, appliedJobs } = useMemo(
    () => extractJobsFromUserState(userState ?? {}),
    [userState]
  );

  return useMemo(() => {
    const labels = {
      browsed: formatMessage({
        id: 'tools.jobFlowSankey.browsed',
        defaultMessage: 'Browsed',
      }),
      viewed: formatMessage({
        id: 'tools.jobFlowSankey.viewed',
        defaultMessage: 'Viewed',
      }),
      saved: formatMessage({
        id: 'tools.jobFlowSankey.saved',
        defaultMessage: 'Saved',
      }),
      applied: formatMessage({
        id: 'tools.jobFlowSankey.applied',
        defaultMessage: 'Applied',
      }),
      interviewing: formatMessage({
        id: 'tools.jobFlowSankey.interviewing',
        defaultMessage: 'Interviewing',
      }),
      offer: formatMessage({
        id: 'tools.jobFlowSankey.offer',
        defaultMessage: 'Offer',
      }),
      rejected: formatMessage({
        id: 'tools.jobFlowSankey.rejected',
        defaultMessage: 'Rejected',
      }),
      hired: formatMessage({
        id: 'tools.jobFlowSankey.hired',
        defaultMessage: 'Hired',
      }),
    };

    const browsedJobs = extractBrowsedJobsFromUserState(userState);
    const raw = buildJobFlowSankey(
      browsedJobs,
      viewedJobs.map((j) => ({ id: j.id, applicationState: j.applicationState })),
      savedJobs.map((j) => ({ id: j.id, applicationState: j.applicationState })),
      appliedJobs.map((j) => ({ id: j.id, applicationState: j.applicationState })),
      labels
    );

    if (!hasAnyPositiveLink(raw)) {
      return { scaledData: null, hasData: false };
    }

    return { scaledData: scaleSankeyData(raw), hasData: true };
  }, [intl, viewedJobs, savedJobs, appliedJobs, userState]);
}
